import { z } from 'zod';
import { HashKind, Id, Int64, Sha256Hex, Timestamp, Uri } from './common.js';

/** Hard upper bound on a single file (02 §2): 50 GiB. */
export const UPLOAD_MAX_FILE_BYTES = 53_687_091_200;
export const UPLOAD_MAX_PARTS = 10_000;

/** Client-side pre-upload facts (02 §2). */
export const UploadFileDescriptor = z.object({
  filename: z.string().min(1).max(1024),
  mime: z.string(),
  size_bytes: z.int().min(1).max(UPLOAD_MAX_FILE_BYTES),
  sha256: Sha256Hex,
  hash_kind: HashKind,
  width: z.int().min(1).optional(),
  height: z.int().min(1).optional(),
  duration_ms: z.int().min(0).optional(),
  last_modified: Timestamp.optional(),
  client_upload_id: Id,
});
export type UploadFileDescriptor = z.infer<typeof UploadFileDescriptor>;

export const UploadSessionCreate = UploadFileDescriptor.extend({
  board_id: Id,
  asset_id: Id.nullable().optional(),
  activate_on_ready: z.boolean().default(true),
});
export type UploadSessionCreate = z.infer<typeof UploadSessionCreate>;

export const UploadPart = z.object({
  part_number: z.int().min(1).max(UPLOAD_MAX_PARTS),
  url: Uri,
  expires_at: Timestamp,
});
export type UploadPart = z.infer<typeof UploadPart>;

export const UploadPartReceived = z.object({
  part_number: z.int(),
  etag: z.string(),
  size_bytes: Int64,
});
export type UploadPartReceived = z.infer<typeof UploadPartReceived>;

export const UploadSessionState = z.enum(['open', 'completing', 'completed', 'aborted']);
export type UploadSessionState = z.infer<typeof UploadSessionState>;

export const UploadSession = z.object({
  upload_id: Id,
  client_upload_id: Id,
  asset_id: Id,
  version_id: Id,
  version_number: z.int().optional(),
  board_id: Id,
  part_size: z.int(),
  part_count: z.int().min(1).max(UPLOAD_MAX_PARTS),
  parts: z.array(UploadPart),
  state: UploadSessionState,
  existing_duplicate: z.object({ asset_id: Id }).nullable().optional(),
  expires_at: Timestamp.optional(),
  created_at: Timestamp,
});
export type UploadSession = z.infer<typeof UploadSession>;

export const UploadSessionStatus = UploadSession.extend({
  received_parts: z.array(UploadPartReceived),
  bytes_received: Int64.optional(),
});
export type UploadSessionStatus = z.infer<typeof UploadSessionStatus>;

export const UploadPartsRefreshRequest = z.object({
  part_numbers: z.array(z.int().min(1).max(UPLOAD_MAX_PARTS)).min(1).max(1000),
});
export type UploadPartsRefreshRequest = z.infer<typeof UploadPartsRefreshRequest>;

export const UploadCompleteRequest = z.object({
  parts: z
    .array(
      z.object({
        part_number: z.int().min(1),
        etag: z.string(),
      }),
    )
    .min(1)
    .max(UPLOAD_MAX_PARTS),
});
export type UploadCompleteRequest = z.infer<typeof UploadCompleteRequest>;
